'use client';

import classNames from 'classnames';
import { ComponentProps } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import InfoDividerDot from '@alltrails/core/components/InfoDividerDot';
import Typography from '@alltrails/core/components/Typography';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import getActivityType from '@alltrails/core/utils/getActivityType';
import { minutesToFormattedTime, monthDayYear } from '@alltrails/data-formatting/dateTimeFormatters';
import { getFormattedDistance, getFormattedElevation } from '@alltrails/data-formatting/distanceFormatters';
import Link from '@alltrails/denali/components/Link';
import type { Activity } from '@alltrails/types/Activity';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { ActivityCardVariant } from '../../types';
import { getTrailUrl } from '../../utils/cardLinks';
import styles from './styles/styles.module.scss';

const activityMessages = defineMessages({
  AT_MEMBER: {
    defaultMessage: 'AllTrails member'
  },
  DISTANCE: {
    defaultMessage: 'Distance'
  },
  ELV_GAIN: {
    defaultMessage: 'Elev. gain'
  },
  TIME: {
    defaultMessage: 'Time'
  }
});

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.stat}>
    <span className={styles.label}>{label}</span>
    <span className={styles.value}>{value}</span>
  </div>
);

const StatDivider = () => <div className={styles.statDivider} />;

type ActivityMetadataProps = {
  variant: ActivityCardVariant;
  activity: Activity;
  isFirstParty?: boolean;
  languageRegionCode: LanguageRegionCode;
  useMetric?: boolean;
  openInNewTab?: boolean;
  baseUrl?: string;
  showActivityType?: boolean;
  showAnonymizedUser?: boolean;
  showOnlyActivityType?: boolean;
  averageTimeToComplete?: {
    value: string;
    unit: string;
  };
  headingComponent?: Extract<ComponentProps<typeof Typography>['component'], 'h1' | 'h2' | 'h3' | 'h4'>;
};

const ActivityMetadata = ({
  variant,
  activity,
  isFirstParty,
  languageRegionCode,
  useMetric = false,
  openInNewTab,
  baseUrl,
  showActivityType,
  showAnonymizedUser,
  showOnlyActivityType,
  averageTimeToComplete,
  headingComponent
}: ActivityMetadataProps) => {
  const { name, minutes, meters, elevationGain, createdAt, activityType, user, trail, locationName, attribution } = activity;
  const {
    intl,
    formattedDefaultMessages: { AT_MEMBER, DISTANCE, ELV_GAIN, TIME }
  } = useFormatMessage(activityMessages);

  const activityLabel = getActivityType(intl, activityType);
  const testIdPrefix = `ActivityCard_${name}`;

  let secondaryInfo;
  if (variant === 'feed') {
    secondaryInfo = (
      <>
        {trail ? (
          <Link
            className={styles.link}
            size="sm"
            variant="secondary"
            href={getTrailUrl(trail.slug, baseUrl)}
            target={openInNewTab ? '_blank' : undefined}
          >
            {trail.name}
          </Link>
        ) : null}
        {trail && locationName ? <InfoDividerDot /> : null}
        <span>{locationName}</span>
      </>
    );
  } else if (showOnlyActivityType) {
    secondaryInfo = activityLabel;
  } else {
    secondaryInfo = (
      <>
        {isFirstParty || !user ? (showAnonymizedUser ? AT_MEMBER : monthDayYear(createdAt, languageRegionCode)) : user.name}
        {variant === 'small' && !showActivityType ? null : (
          <>
            <InfoDividerDot />
            {activityLabel}
          </>
        )}
      </>
    );
  }

  return (
    <div className={classNames(styles.container, styles[variant])}>
      <div className={classNames(styles.title, styles.truncating)} data-testid={`${testIdPrefix}_Title`}>
        {headingComponent ? (
          <Typography component={headingComponent} variant="text200bold" className={styles.cardTitleWithHeadingComponent}>
            {name}
          </Typography>
        ) : (
          name
        )}
      </div>
      <div className={classNames(styles.secondaryInfo, styles.truncating)} data-testid={`${testIdPrefix}_Subtext`}>
        {secondaryInfo}
      </div>
      {attribution && variant !== 'small' && (
        <Typography variant="text100" component="div" className={styles.attribution}>
          <FormattedMessage defaultMessage="Recorded with {attribution}" values={{ attribution }} />
        </Typography>
      )}
      <div className={styles.stats} data-testid={`${testIdPrefix}_Stats`}>
        <Stat label={DISTANCE} value={getFormattedDistance(intl, meters, useMetric)} />
        <StatDivider />
        <Stat label={ELV_GAIN} value={getFormattedElevation(intl, elevationGain, useMetric)} />
        {variant === 'small' ? null : (
          <>
            <StatDivider />
            {averageTimeToComplete ? (
              <Stat label={TIME} value={`${averageTimeToComplete.value} ${averageTimeToComplete.unit}`} />
            ) : (
              <Stat label={TIME} value={minutesToFormattedTime({ intl, totalMinutes: minutes })} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityMetadata;
